// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isDefined } from '@shared/utils';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectClient } from '@shared/data-access/slices/clients/clients-selectors';
import type {
  SuitabilityProfileType,
  SuitabilityProfileMetadata,
} from '@shared/advisory/account-workflow/utils/suitability-profile';
import { getSuitabilityProfileMetadata } from '@shared/advisory/account-workflow/utils/suitability-profile';

export interface ClientProfileLinkProps {
  /** Indicates whether the sidebar is expanded or collapsed. Default is `false` */
  readonly expanded?: boolean;

  /** Indicates whether the link should be hidden. Default is `false` */
  readonly hidden?: boolean;
}

/**
 * Custom hook for managing the client profile link component state.
 *
 * This hook provides functionality to get the selected client's risk profile and
 * its metadata for displaying in the navigation link.
 */
export const useClientProfileLink = (props: ClientProfileLinkProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const activeClient = useSelector(selectClient);

  const expanded = props.expanded ?? false;
  const hidden = props.hidden ?? false;

  const isActive = useMemo(
    () => location.pathname === '/profile',
    [location.pathname],
  );

  const riskProfile = useMemo(
    () => activeClient?.risk_profile ?? null,
    [activeClient?.risk_profile],
  );

  const profileMetadata = useMemo<SuitabilityProfileMetadata | null>(() => {
    if (!riskProfile) return null;

    const validProfileTypes: SuitabilityProfileType[] = [
      'conservative',
      'moderate',
      'dynamic',
      'aggressive',
    ];

    const normalizedProfile = riskProfile.toLowerCase() as SuitabilityProfileType;

    if (validProfileTypes.includes(normalizedProfile)) {
      return getSuitabilityProfileMetadata(normalizedProfile);
    }

    return null;
  }, [riskProfile]);

  const hasRiskProfile = useMemo(
    () => isDefined(riskProfile) && profileMetadata !== null,
    [riskProfile, profileMetadata],
  );

  const chipLabel = useMemo(() => {
    if (!profileMetadata) return '';
    return t(profileMetadata.titleKey as any, { ns: 'components' });
  }, [profileMetadata, t]);

  return {
    riskProfile,
    profileMetadata,
    hasRiskProfile,
    chipLabel,
    isActive,
    expanded,
    hidden,
  } as const;
};
